import { gql, useMutation, useQuery } from '@apollo/client'
import { Button, FlexRow } from '@unpublished/common-components'
import React, { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import {
  Breadcrumb,
  FixedWidthPageContainer,
  SpanIndentedWrappingFlexDiv,
} from '../../common/common-components'
import { FixedWidthTable, TableRow } from '../../common/flex-table'
import {
  DoRegenerateViewsMutation,
  DoRegenerateViewsMutationVariables,
  RegenerateViewsQuery,
  RegenerateViewsQueryVariables,
} from '../../common/generated'
import {
  PromptActionButton,
  PromptContainer,
  PromptTextContainer,
} from '../../common/prompt'
import { ToggleableButtonList } from '../../common/toggleable-button-list'
import { useNumericParam } from '../../common/use-numeric-param'
import { useIterationPicker } from '../iteration-picker'
import { MeasurementName } from '../measure-again/view-model'
import { createViewModel, REGENERATE_VIEWS_QUERY } from './view-model'

const SelectContainer = styled.div`
  width: 15em;
  display: inline-block;
`

export function RegenerateViews(): JSX.Element {
  const navigate = useNavigate()

  const measurementStudyId = useNumericParam('selectedStudy')

  const [measurementNames, setMeasurementNames] = useState<
    Set<MeasurementName>
  >(new Set())

  const { measurementInvocationIteration, renderIterationPicker } =
    useIterationPicker({ measurementStudyId })

  const { data } = useQuery<
    RegenerateViewsQuery,
    RegenerateViewsQueryVariables
  >(REGENERATE_VIEWS_QUERY, {
    variables: { measurementStudyId },
  })

  const viewModel = data
    ? createViewModel({
        data,
        measurementInvocationIteration,
        measurementNames,
      })
    : undefined

  function goToStudy(): void {
    navigate(`/studies/${measurementStudyId}`)
  }

  const [regenerateViews, { error: regenerateViewsError, loading }] =
    useMutation<DoRegenerateViewsMutation, DoRegenerateViewsMutationVariables>(
      gql`
        mutation DoRegenerateViews($input: RegenerateViewsInput!) {
          regenerateViews(input: $input) {
            numberOfTranslatedViewsDeleted
          }
        }
      `,
      {
        variables: {
          input: {
            measurementNames: Array.from(measurementNames),
            measurementStudyId,
            measurementInvocationIteration: measurementInvocationIteration ?? 0,
          },
        },
      }
    )

  const canRegenerateViews = !loading && measurementNames.size > 0

  async function handleRegenerateViews(): Promise<void> {
    await regenerateViews()
    goToStudy()
  }

  return (
    <FixedWidthPageContainer>
      <FlexRow>
        <Breadcrumb>
          <Link to="/">Home</Link> {'>'}{' '}
          <Link to="/studies">Measurement studies</Link> {'>'}{' '}
          <Link to={`/studies/${measurementStudyId}`}>
            {viewModel?.measurementStudyName}
          </Link>{' '}
          {'>'} Regenerate Views
        </Breadcrumb>
      </FlexRow>
      <h1>{viewModel?.measurementStudyName}</h1>
      {viewModel ? (
        <>
          <h2>Regenerate Views</h2>
          <FixedWidthTable>
            <TableRow>
              <SpanIndentedWrappingFlexDiv>
                <span>Iteration</span>
                <span>
                  <SelectContainer>{renderIterationPicker()}</SelectContainer>
                </span>
              </SpanIndentedWrappingFlexDiv>
            </TableRow>
          </FixedWidthTable>{' '}
          <h2>Choose measurements</h2>
          {viewModel.measurementNamesForSelectedMeasurementInvocationIteration &&
            measurementNames && (
              <ToggleableButtonList
                options={
                  viewModel.measurementNamesForSelectedMeasurementInvocationIteration
                }
                onChange={setMeasurementNames}
                selectedOptionValues={measurementNames}
              />
            )}
          <PromptContainer>
            <PromptTextContainer>
              {viewModel?.numScreenshotsToRegenerate} views will be regenerated
              for {viewModel?.numSelectedMeasurementInvocations} selected
              measurement invocations
            </PromptTextContainer>
            <span>
              <Button onClick={goToStudy} disabled={!canRegenerateViews}>
                No, don't regenerate
              </Button>
              <PromptActionButton
                onClick={handleRegenerateViews}
                disabled={!canRegenerateViews}
              >
                Regenerate
              </PromptActionButton>
            </span>
          </PromptContainer>
        </>
      ) : (
        <>
          <div>Loading&hellip;</div>
          <div>
            <Button onClick={goToStudy}>Cancel</Button>
          </div>
        </>
      )}
      {regenerateViewsError && <p>Error: {regenerateViewsError.message}</p>}
    </FixedWidthPageContainer>
  )
}
